/**
 * Wave 10 — UploadQueue.
 *
 * A thin, headless-ish in-memory queue for attachments destined for R2.
 * Real uploads happen via a presigned-POST shape in Wave 7's attachment
 * service — this component is the visible half: it shows the user which
 * files are pending, in-flight, failed, or done, and gives a one-tap
 * retry for failures.
 *
 * We deliberately keep state *above* the component (a tiny Zustand
 * store) so the same queue is visible in multiple surfaces (RDO page,
 * NF uploader, ART uploader) without prop drilling. The store itself
 * doesn't do the uploading — a caller pushes a "task" with an upload
 * function, and this queue awaits it.
 *
 * Retries use exponential backoff: 500ms, 1.5s, 4s (then stop and show
 * failure). We cap concurrency at 2 so a slow phone uplink doesn't
 * stampede the device.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

type TaskStatus = 'queued' | 'uploading' | 'done' | 'error';

interface UploadTask {
  id: string;
  label: string;
  sizeBytes: number;
  status: TaskStatus;
  progress: number; // 0..1
  attempts: number;
  error?: string;
  /** The caller supplies the actual upload function. */
  upload: (onProgress: (p: number) => void) => Promise<void>;
}

interface QueueState {
  tasks: UploadTask[];
  enqueue: (task: Omit<UploadTask, 'status' | 'progress' | 'attempts'>) => void;
  retry: (id: string) => void;
  clearDone: () => void;
  _setStatus: (id: string, patch: Partial<UploadTask>) => void;
}

const MAX_CONCURRENT = 2;
const BACKOFF_MS = [500, 1500, 4000];

export const useUploadQueue = create<QueueState>((set, get) => ({
  tasks: [],
  enqueue: (task) =>
    set((s) => ({
      tasks: [
        ...s.tasks,
        { ...task, status: 'queued', progress: 0, attempts: 0 },
      ],
    })),
  retry: (id) =>
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === id ? { ...t, status: 'queued', error: undefined } : t,
      ),
    })),
  clearDone: () =>
    set((s) => ({ tasks: s.tasks.filter((t) => t.status !== 'done') })),
  _setStatus: (id, patch) =>
    set((s) => ({
      tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })),
}));

/**
 * Runner — a singleton effect that picks up queued tasks and runs them
 * respecting the concurrency cap. Mounted once (inside `<UploadQueue />`).
 */
function useQueueRunner() {
  useEffect(() => {
    let cancelled = false;

    async function tick() {
      if (cancelled) return;
      const { tasks, _setStatus } = useUploadQueue.getState();
      const inFlight = tasks.filter((t) => t.status === 'uploading').length;
      const slot = MAX_CONCURRENT - inFlight;
      if (slot <= 0) return;

      const next = tasks.filter((t) => t.status === 'queued').slice(0, slot);
      for (const task of next) {
        _setStatus(task.id, {
          status: 'uploading',
          attempts: task.attempts + 1,
          progress: 0,
        });
        task
          .upload((p) => _setStatus(task.id, { progress: Math.min(1, p) }))
          .then(() =>
            _setStatus(task.id, { status: 'done', progress: 1 }),
          )
          .catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : String(err);
            const attempts = task.attempts + 1;
            if (attempts <= BACKOFF_MS.length) {
              const delay = BACKOFF_MS[attempts - 1];
              setTimeout(() => {
                if (cancelled) return;
                _setStatus(task.id, { status: 'queued' });
              }, delay);
            } else {
              _setStatus(task.id, { status: 'error', error: msg });
            }
          });
      }
    }

    const unsubscribe = useUploadQueue.subscribe(tick);
    // Kick once on mount in case there are already queued tasks.
    tick();
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);
}

export function UploadQueue() {
  const { t } = useT();
  useQueueRunner();
  const tasks = useUploadQueue((s) => s.tasks);
  const retry = useUploadQueue((s) => s.retry);
  const clearDone = useUploadQueue((s) => s.clearDone);

  const visible = tasks.filter((x) => x.status !== 'done' || tasks.length < 8);
  if (visible.length === 0) return null;

  const active = tasks.filter(
    (x) => x.status === 'queued' || x.status === 'uploading',
  ).length;
  const errored = tasks.filter((x) => x.status === 'error').length;

  return (
    <aside
      aria-label={t.wave10.uploads.title}
      className={cn(
        'fixed bottom-20 right-3 z-20 w-[min(22rem,90vw)] animate-fade-in-up md:bottom-4',
        'rounded-xl border border-line bg-surface-2 shadow-pop dark:shadow-pop-dark',
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold text-ink">
          {active > 0
            ? t.wave10.uploads.active(active)
            : errored > 0
              ? t.wave10.uploads.errored(errored)
              : t.wave10.uploads.done}
        </h2>
        <button
          type="button"
          onClick={clearDone}
          className="text-[11px] text-ink-muted hover:text-ink"
        >
          {t.wave10.uploads.clearDone}
        </button>
      </header>
      <ul className="max-h-60 space-y-1.5 overflow-auto px-3 py-2">
        {visible.map((task) => (
          <li
            key={task.id}
            className="rounded-lg border border-line bg-surface px-2 py-1.5"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-xs font-medium text-ink">
                {task.label}
              </p>
              <span className="shrink-0 text-[11px] tabular-nums text-ink-subtle">
                {formatBytes(task.sizeBytes)}
              </span>
            </div>
            {task.status === 'uploading' && (
              <ProgressBar
                value={Math.round(task.progress * 100)}
                className="mt-1"
              />
            )}
            {task.status === 'queued' && (
              <p className="mt-1 text-[11px] text-ink-muted">
                {t.wave10.uploads.queued}
              </p>
            )}
            {task.status === 'error' && (
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="truncate text-[11px] text-err">{task.error}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => retry(task.id)}
                >
                  {t.common.retry}
                </Button>
              </div>
            )}
            {task.status === 'done' && (
              <p className="mt-1 text-[11px] text-ok">
                {t.wave10.uploads.uploaded}
              </p>
            )}
          </li>
        ))}
      </ul>
    </aside>
  );
}

function formatBytes(n: number): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
